import { isEmpty } from 'lodash';

export const flattenObject = <T extends Record<string, any> = Record<string, any>>(
  obj: T,
  parentKey = '',
): T => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    const newKey = parentKey ? `${parentKey}_${key}` : key;

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(acc, flattenObject(value, newKey)); // flattens recursively
    } else {
      acc[newKey as keyof T] = value; // returns simple values
    }

    return acc;
  }, {} as T);
};

export function deepValueToString(value: any): string {
  if (Array.isArray(value)) {
    return value.map(deepValueToString).join(' ');
  } else if (value && typeof value === 'object') {
    return Object.values(value).map(deepValueToString).join(' ');
  } else {
    return String(value ?? '');
  }
}

export function emptyToUndefined<T extends Record<string, any> | undefined>(obj: T): T | undefined {
  if (!obj) return undefined;
  return isEmpty(obj) ? undefined : obj;
}

/**
 * Safely parse a JSON string or return the object itself.
 */
export function toObject<T extends object = Record<string, unknown>>(maybeJson: unknown): T {
  if (!maybeJson) return {} as T;
  if (typeof maybeJson === 'string') {
    try {
      return JSON.parse(maybeJson) as T;
    } catch {
      return {} as T;
    }
  }
  return maybeJson as T;
}

/**
 * Merge a single entry into an existing object (or JSON string).
 */
export function mergeEntryIntoObject<T extends object>(
  current: unknown,
  entryKey: string,
  entryValue: unknown,
): T {
  const base = toObject<T>(current);
  return { ...base, [entryKey]: entryValue };
}
